import React, { useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';
import { submitVerificationApplication } from '../services/verificationApi';

// .passthrough() matters here: this resolver is used for the whole multi-step
// form (not swapped per step), and Zod object schemas strip unrecognized keys
// by default — without passthrough, handleSubmit would only ever see
// `partnerType` and silently drop every other field the applicant filled in.
const partnerTypeSchema = z
  .object({
    partnerType: z.string().regex(/^(GUIDE|OPERATOR)$/, 'Select a valid partner type'),
  })
  .passthrough();

const personalInfoSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dob: z.string().min(1, 'Date of birth is required'),
  gender: z.string().regex(/^(Male|Female|Other)$/, 'Select a valid gender'),
  nationality: z.string().min(1, 'Nationality is required'),
  country: z.string().min(1, 'Country is required'),
  state: z.string().min(1, 'County/State is required'),
  city: z.string().min(1, 'City is required'),
  address: z.string().min(1, 'Residential address is required'),
  phone: z.string().min(1, 'Phone number is required'),
  whatsapp: z.string().optional(),
  email: z.string().email('Enter a valid email'),
  profilePhoto: z.any().optional(),
});

const guideInfoSchema = z.object({
  guideName: z.string().min(1, 'Guide name is required'),
  experienceYears: z.number().min(0, 'Years of experience is required'),
  languages: z.string().min(1, 'Languages spoken is required'),
  expertise: z.string().min(1, 'Areas of expertise are required'),
  parks: z.string().min(1, 'National parks covered are required'),
  bio: z.string().min(1, 'Short biography is required'),
  dailyRate: z.string().optional(),
  website: z.string().optional(),
  socialLinks: z.string().optional(),
});

const operatorInfoSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  registrationNumber: z.string().min(1, 'Registration number is required'),
  taxNumber: z.string().optional(),
  businessAddress: z.string().min(1, 'Business address is required'),
  country: z.string().min(1, 'Country is required'),
  state: z.string().min(1, 'County/State is required'),
  city: z.string().min(1, 'City is required'),
  website: z.string().url('Enter a valid website').optional(),
  description: z.string().min(1, 'Company description is required'),
  employees: z.number().min(1, 'Number of employees is required'),
  yearsInOperation: z.number().min(0, 'Years in operation is required'),
  services: z.string().min(1, 'Main services offered are required'),
});

const servicesSchema = z.object({
  services: z.array(z.string()).nonempty('Select at least one service'),
  customService: z.string().optional(),
});

const operatingAreasSchema = z.object({
  countries: z.array(z.string()).nonempty('Select at least one country'),
  areas: z.array(z.string()).nonempty('Select at least one operating area'),
});

const documentsSchema = z.object({
  documents: z.any().optional(),
});

const bankingSchema = z.object({
  bankName: z.string().optional(),
  accountName: z.string().optional(),
  accountNumber: z.string().optional(),
  mobileMoney: z.string().optional(),
  currency: z.string().optional(),
});

const referencesSchema = z.object({
  reference1Name: z.string().min(1, 'Reference name is required'),
  reference1Organization: z.string().min(1, 'Organization is required'),
  reference1Email: z.string().email('Enter a valid email'),
  reference1Phone: z.string().min(1, 'Phone is required'),
  reference1Relationship: z.string().min(1, 'Relationship is required'),
  reference2Name: z.string().min(1, 'Reference name is required'),
  reference2Organization: z.string().min(1, 'Organization is required'),
  reference2Email: z.string().email('Enter a valid email'),
  reference2Phone: z.string().min(1, 'Phone is required'),
  reference2Relationship: z.string().min(1, 'Relationship is required'),
});

const reviewSchema = z.object({
  agreeTerms: z.boolean().refine((value) => value === true, 'You must agree to the terms'),
  agreePrivacy: z.boolean().refine((value) => value === true, 'You must agree to the privacy policy'),
  agreeConduct: z.boolean().refine((value) => value === true, 'You must agree to the code of conduct'),
  agreeTourism: z.boolean().refine((value) => value === true, 'You must agree to the tourism guidelines'),
  declaration: z.boolean().refine((value) => value === true, 'You must accept the declaration'),
});

const stepTitles = [
  'Partner Type',
  'Personal Information',
  'Business Information',
  'Services',
  'Operating Areas',
  'Documents',
  'Banking & Payments',
  'References',
  'Review',
  'Submit',
];

const allServices = [
  'Wildlife Safaris',
  'Cultural Tours',
  'Bird Watching',
  'Hiking',
  'Mountain Climbing',
  'Camping',
  'Photography Tours',
  'Beach Holidays',
  'Luxury Safaris',
  'Family Tours',
  'Student Tours',
  'Community Tourism',
  'Custom Packages',
];

const countryOptions = ['Kenya', 'Tanzania', 'Uganda', 'Rwanda'];
const areaOptions = ['Amboseli', 'Maasai Mara', 'Serengeti', 'Ngorongoro', 'Mount Kilimanjaro'];

export default function PartnerApplyPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const methods = useForm({
    resolver: zodResolver(partnerTypeSchema),
    defaultValues: {
      partnerType: 'GUIDE',
      firstName: '',
      lastName: '',
      dob: '',
      gender: 'Male',
      nationality: '',
      country: '',
      state: '',
      city: '',
      address: '',
      phone: '',
      whatsapp: '',
      email: '',
      profilePhoto: null,
      guideName: '',
      experienceYears: 0,
      languages: '',
      expertise: '',
      parks: '',
      bio: '',
      dailyRate: '',
      website: '',
      socialLinks: '',
      businessName: '',
      registrationNumber: '',
      taxNumber: '',
      businessAddress: '',
      employees: 0,
      yearsInOperation: 0,
      description: '',
      services: [],
      customService: '',
      countries: [],
      areas: [],
      documents: null,
      bankName: '',
      accountName: '',
      accountNumber: '',
      mobileMoney: '',
      currency: '',
      reference1Name: '',
      reference1Organization: '',
      reference1Email: '',
      reference1Phone: '',
      reference1Relationship: '',
      reference2Name: '',
      reference2Organization: '',
      reference2Email: '',
      reference2Phone: '',
      reference2Relationship: '',
      agreeTerms: false,
      agreePrivacy: false,
      agreeConduct: false,
      agreeTourism: false,
      declaration: false,
    },
  });

  const currentPartnerType = methods.watch('partnerType');
  const watchServices = methods.watch('services');

  const stepLabel = useMemo(() => stepTitles[step], [step]);

  function handleNext() {
    setStep((prev) => Math.min(prev + 1, stepTitles.length - 1));
  }

  function handlePrevious() {
    setStep((prev) => Math.max(prev - 1, 0));
  }

  async function onSubmit(data: any) {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const role = data.partnerType === 'OPERATOR' ? 'operator' : 'guide';
      const formData = new FormData();
      formData.append('role', role);

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'profilePhoto' || key === 'documents' || value == null) return;
        if (Array.isArray(value)) {
          value.forEach((v) => formData.append(key, String(v)));
        } else {
          formData.append(key, String(value));
        }
      });

      const profilePhotoList = data.profilePhoto as FileList | undefined;
      if (profilePhotoList && profilePhotoList.length > 0) {
        formData.append('profilePhoto', profilePhotoList[0]);
      }

      const documentsList = data.documents as FileList | undefined;
      if (documentsList) {
        Array.from(documentsList).forEach((file) => formData.append('documents', file));
      }

      await submitVerificationApplication(formData);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.message || 'Something went wrong submitting your application.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="page partnerApplyPage">
        <div className="container">
          <PageHeader
            kicker="Partner Verification"
            title="Application submitted"
            subtitle="Our team reviews every application against our community-benefit standards before approving a partner account."
          />
          <Card title="What happens next">
            <p style={{ marginTop: 0, lineHeight: 1.7 }}>
              Your account stays inactive until an administrator reviews your details and
              documents. We'll notify you by email once a decision is made — most applications
              are reviewed within a few business days.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="page partnerApplyPage">
      <div className="container">
        <PageHeader
          kicker="Partner Verification"
          title="Apply to join SafariCo Hub as a verified partner"
          subtitle="We vet every guide and operator against our community-benefit standards before they join the network our travelers and donors trust. Complete the application below to be reviewed by our team."
        />

        <div className="wizard">
          <div className="wizardHeader">
            <div className="wizardProgress">Step {step + 1} of {stepTitles.length}</div>
            <div className="wizardTitle">{stepLabel}</div>
          </div>

          <div className="wizardSteps">
            {stepTitles.map((title, index) => (
              <div key={title} className={`wizardStep ${index === step ? 'active' : index < step ? 'completed' : ''}`}>
                <span>{index + 1}</span>
                <div>{title}</div>
              </div>
            ))}
          </div>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="wizardContent">
                {step === 0 && (
                  <div className="grid-2 gap-16">
                    <label className={`card selectable ${currentPartnerType === 'GUIDE' ? 'selected' : ''}`}>
                      <input type="radio" value="GUIDE" {...methods.register('partnerType')} />
                      <h3>Local Guide</h3>
                      <p>Individual guide leading walking tours, wildlife tours, cultural experiences. No company required.</p>
                    </label>
                    <label className={`card selectable ${currentPartnerType === 'OPERATOR' ? 'selected' : ''}`}>
                      <input type="radio" value="OPERATOR" {...methods.register('partnerType')} />
                      <h3>Tour Operator</h3>
                      <p>Registered company offering safari packages, managing bookings, and employing guides.</p>
                    </label>
                  </div>
                )}

                {step === 1 && (
                  <div className="formSection">
                    <div className="fieldGrid">
                      <label>
                        First Name
                        <input type="text" {...methods.register('firstName')} />
                      </label>
                      <label>
                        Last Name
                        <input type="text" {...methods.register('lastName')} />
                      </label>
                    </div>
                    <div className="fieldGrid">
                      <label>
                        Date of Birth
                        <input type="date" {...methods.register('dob')} />
                      </label>
                      <label>
                        Gender
                        <select {...methods.register('gender')}>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </label>
                    </div>
                    <div className="fieldGrid">
                      <label>
                        Nationality
                        <input type="text" {...methods.register('nationality')} />
                      </label>
                      <label>
                        Country
                        <input type="text" {...methods.register('country')} />
                      </label>
                    </div>
                    <div className="fieldGrid">
                      <label>
                        County/State
                        <input type="text" {...methods.register('state')} />
                      </label>
                      <label>
                        City
                        <input type="text" {...methods.register('city')} />
                      </label>
                    </div>
                    <label>
                      Residential Address
                      <input type="text" {...methods.register('address')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Phone Number
                        <input type="tel" {...methods.register('phone')} />
                      </label>
                      <label>
                        WhatsApp Number (optional)
                        <input type="tel" {...methods.register('whatsapp')} />
                      </label>
                    </div>
                    <label>
                      Email Address
                      <input type="email" {...methods.register('email')} />
                    </label>
                    <label>
                      Profile Photo Upload
                      <input type="file" accept="image/png,image/jpeg" {...methods.register('profilePhoto')} />
                    </label>
                  </div>
                )}

                {step === 2 && currentPartnerType === 'GUIDE' && (
                  <div className="formSection">
                    <label>
                      Guide Name
                      <input type="text" {...methods.register('guideName')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Years of Experience
                        <input type="number" {...methods.register('experienceYears', { valueAsNumber: true })} />
                      </label>
                      <label>
                        Languages Spoken
                        <input type="text" {...methods.register('languages')} />
                      </label>
                    </div>
                    <label>
                      Areas of Expertise
                      <textarea {...methods.register('expertise')} />
                    </label>
                    <label>
                      National Parks Covered
                      <input type="text" {...methods.register('parks')} />
                    </label>
                    <label>
                      Short Biography
                      <textarea {...methods.register('bio')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Daily Rate (optional)
                        <input type="text" {...methods.register('dailyRate')} />
                      </label>
                      <label>
                        Website (optional)
                        <input type="url" {...methods.register('website')} />
                      </label>
                    </div>
                    <label>
                      Social Media Links (optional)
                      <input type="text" {...methods.register('socialLinks')} />
                    </label>
                  </div>
                )}

                {step === 2 && currentPartnerType === 'OPERATOR' && (
                  <div className="formSection">
                    <label>
                      Business Name
                      <input type="text" {...methods.register('businessName')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Registration Number
                        <input type="text" {...methods.register('registrationNumber')} />
                      </label>
                      <label>
                        Tax Number (optional)
                        <input type="text" {...methods.register('taxNumber')} />
                      </label>
                    </div>
                    <label>
                      Business Address
                      <input type="text" {...methods.register('businessAddress')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Country
                        <input type="text" {...methods.register('country')} />
                      </label>
                      <label>
                        County/State
                        <input type="text" {...methods.register('state')} />
                      </label>
                    </div>
                    <label>
                      City
                      <input type="text" {...methods.register('city')} />
                    </label>
                    <label>
                      Company Website
                      <input type="url" {...methods.register('website')} />
                    </label>
                    <label>
                      Company Description
                      <textarea {...methods.register('description')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Number of Employees
                        <input type="number" {...methods.register('employees', { valueAsNumber: true })} />
                      </label>
                      <label>
                        Years in Operation
                        <input type="number" {...methods.register('yearsInOperation', { valueAsNumber: true })} />
                      </label>
                    </div>
                    <label>
                      Main Services Offered
                      <textarea {...methods.register('services')} />
                    </label>
                  </div>
                )}

                {step === 3 && (
                  <div className="formSection">
                    <div className="checkboxGrid">
                      {allServices.map((service) => (
                        <label key={service} className="checkboxCard">
                          <input
                            type="checkbox"
                            value={service}
                            {...methods.register('services')}
                          />
                          {service}
                        </label>
                      ))}
                    </div>
                    <label>
                      Add custom service
                      <input type="text" {...methods.register('customService')} />
                    </label>
                  </div>
                )}

                {step === 4 && (
                  <div className="formSection">
                    <label>
                      Countries
                      <select multiple {...methods.register('countries')}>
                        {countryOptions.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Operating Areas
                      <select multiple {...methods.register('areas')}>
                        {areaOptions.map((area) => (
                          <option key={area} value={area}>{area}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                {step === 5 && (
                  <div className="formSection">
                    <label>
                      Upload required documents
                      <input type="file" multiple accept="application/pdf,image/png,image/jpeg" {...methods.register('documents')} />
                    </label>
                    <p className="muted">Accepted formats: PDF, JPG, PNG.</p>
                  </div>
                )}

                {step === 6 && (
                  <div className="formSection">
                    <label>
                      Bank Name
                      <input type="text" {...methods.register('bankName')} />
                    </label>
                    <label>
                      Account Name
                      <input type="text" {...methods.register('accountName')} />
                    </label>
                    <div className="fieldGrid">
                      <label>
                        Account Number
                        <input type="text" {...methods.register('accountNumber')} />
                      </label>
                      <label>
                        Mobile Money Number
                        <input type="text" {...methods.register('mobileMoney')} />
                      </label>
                    </div>
                    <label>
                      Preferred Currency
                      <input type="text" {...methods.register('currency')} />
                    </label>
                  </div>
                )}

                {step === 7 && (
                  <div className="formSection">
                    <div className="fieldGrid">
                      <label>
                        Reference 1 Name
                        <input type="text" {...methods.register('reference1Name')} />
                      </label>
                      <label>
                        Organization
                        <input type="text" {...methods.register('reference1Organization')} />
                      </label>
                    </div>
                    <div className="fieldGrid">
                      <label>
                        Email
                        <input type="email" {...methods.register('reference1Email')} />
                      </label>
                      <label>
                        Phone
                        <input type="text" {...methods.register('reference1Phone')} />
                      </label>
                    </div>
                    <label>
                      Relationship
                      <input type="text" {...methods.register('reference1Relationship')} />
                    </label>

                    <div className="fieldGrid">
                      <label>
                        Reference 2 Name
                        <input type="text" {...methods.register('reference2Name')} />
                      </label>
                      <label>
                        Organization
                        <input type="text" {...methods.register('reference2Organization')} />
                      </label>
                    </div>
                    <div className="fieldGrid">
                      <label>
                        Email
                        <input type="email" {...methods.register('reference2Email')} />
                      </label>
                      <label>
                        Phone
                        <input type="text" {...methods.register('reference2Phone')} />
                      </label>
                    </div>
                    <label>
                      Relationship
                      <input type="text" {...methods.register('reference2Relationship')} />
                    </label>
                  </div>
                )}

                {step === 8 && (
                  <div className="formSection reviewSection">
                    <pre>{JSON.stringify(methods.getValues(), null, 2)}</pre>
                  </div>
                )}

                {step === 9 && (
                  <div className="formSection">
                    <label className="checkboxCard">
                      <input type="checkbox" {...methods.register('agreeTerms')} />
                      I agree to the Terms and Conditions
                    </label>
                    <label className="checkboxCard">
                      <input type="checkbox" {...methods.register('agreePrivacy')} />
                      I agree to the Privacy Policy
                    </label>
                    <label className="checkboxCard">
                      <input type="checkbox" {...methods.register('agreeConduct')} />
                      I agree to the Code of Conduct
                    </label>
                    <label className="checkboxCard">
                      <input type="checkbox" {...methods.register('agreeTourism')} />
                      I agree to the Ethical Tourism Policy
                    </label>
                    <label className="checkboxCard">
                      <input type="checkbox" {...methods.register('declaration')} />
                      I certify that all information provided is accurate.
                    </label>
                  </div>
                )}
              </div>

              {submitError && step === stepTitles.length - 1 && (
                <div className="error" style={{ marginBottom: 12 }}>{submitError}</div>
              )}

              <div className="wizardActions">
                <button type="button" className="btn btnSecondary" onClick={handlePrevious} disabled={step === 0}>Previous</button>
                {step < stepTitles.length - 1 ? (
                  <button type="button" className="btn btnPrimary" onClick={handleNext}>Next</button>
                ) : (
                  <button
                    type="button"
                    className="btn btnPrimary"
                    disabled={submitting}
                    onClick={methods.handleSubmit(onSubmit)}
                  >
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}
